import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute component with guest browsing support
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|null} props.requiredRole - Required user role (null = any authenticated user)
 * @param {boolean} props.requireAuth - If true, requires authentication. If false, allows guest access.
 */
const ProtectedRoute = ({ children, requiredRole = null, requireAuth = true }) => {
  const { currentUser, userRole, hasRole, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9F9F9'
      }}>
        <div style={{
          width: 32,
          height: 32,
          border: '3px solid transparent',
          borderTop: '3px solid #C97B53',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `
        }} />
      </div>
    );
  }

  // If requireAuth is false, allow guest access
  if (!requireAuth) {
    return children;
  }

  // Require authentication
  if (!currentUser) {
    // Redirect to signin with return path
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?returnTo=${returnTo}`} replace />;
  }

  // Check role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    // Access denied - insufficient permissions
    switch (userRole) {
      case 'customer':
        return <Navigate to="/customer-dashboard" replace />;
      case 'creator':
        return <Navigate to="/seller-hub" replace />;
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      default:
        return <Navigate to="/customer-dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute; 